import React, { useState, useMemo, useRef, useEffect } from "react";
import { Tabs, Tab, Typography, Box, TextField, Button, IconButton, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import DateRangeIcon from "@mui/icons-material/DateRange";
import "./AdminUsersPage.scss";
import { Buyer } from "../../../Models/Buyer";
import { Seller } from "../../../Models/Seller";
import { SellerPlan } from "../../../Models/SellerPlan";
import { AdminAPIHandler } from "../../../services/NetworkClient/BackendAPIs/AdminAPIHandler";
import AdminUserCard from "../../../components/PageComponents/AdminComponents/AdminUserCard/AdminUserCard";
import GGLoaderButton from "../../../components/CommonComponents/GGLoaderButton/GGLoaderButton";
import SnackBar, { SnackTypes } from "../../../components/UtilityComponents/SnackBar/SnackBar";
import GGModalWindow from "../../../components/UtilityComponents/GGModalWindow/GGModalWindow";
import { ErrorLogger } from "../../../utils/Logger";

const AdminUsersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Buyers state
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [buyerSearchType, setBuyerSearchType] = useState<'id' | 'email' | 'dateRange' | null>(null);
  const [buyerIdSearch, setBuyerIdSearch] = useState("");
  const [buyerEmailSearch, setBuyerEmailSearch] = useState("");
  const [buyerStartDate, setBuyerStartDate] = useState("");
  const [buyerEndDate, setBuyerEndDate] = useState("");
  const [buyerFilterQuery, setBuyerFilterQuery] = useState("");
  const [isLoadingBuyers, setIsLoadingBuyers] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState<Buyer | null>(null);
  const [showBuyerModal, setShowBuyerModal] = useState(false);

  // Sellers state
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [sellerSearchType, setSellerSearchType] = useState<'id' | 'email' | 'dateRange' | null>(null);
  const [sellerIdSearch, setSellerIdSearch] = useState("");
  const [sellerEmailSearch, setSellerEmailSearch] = useState("");
  const [sellerStartDate, setSellerStartDate] = useState("");
  const [sellerEndDate, setSellerEndDate] = useState("");
  const [sellerFilterQuery, setSellerFilterQuery] = useState("");
  const [isLoadingSellers, setIsLoadingSellers] = useState(false);
  const [editingSeller, setEditingSeller] = useState<Seller | null>(null);
  const [showSellerModal, setShowSellerModal] = useState(false);

  // Form states
  const [buyerFormState, setBuyerFormState] = useState<any>({
    fullName: "",
    email: "",
    mobileNo: "",
    about: "",
  });

  const [sellerFormState, setSellerFormState] = useState<any>({
    fullName: "",
    email: "",
    occupation: "",
    personalDescription: "",
    tagline: "",
    contactNumber: "",
    gender: "",
    planDetails: null,
  });

  // Seller Plans state
  const [availablePlans, setAvailablePlans] = useState<SellerPlan[]>([]);
  const [, setIsLoadingPlans] = useState(false);

  // Snackbar
  const snackBarRef = useRef<{ showSnackbar(): any }>(null);
  const [snackBarParams, setSnackBarParams] = useState({ message: "", state: SnackTypes.success });
  const notify = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message, state });
    snackBarRef.current?.showSnackbar();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);

  // Load active seller plans on component mount
  useEffect(() => {
    const loadPlans = async () => {
      setIsLoadingPlans(true);
      try {
        const plans = await AdminAPIHandler.shared.getActiveSellerPlans();
        setAvailablePlans(plans);
      } catch (e) {
        ErrorLogger.log(e, 'AdminUsersPage.loadPlans');
      }
      setIsLoadingPlans(false);
    };
    loadPlans();
  }, []);

  // ========== Buyer Functions ==========
  
  const fetchBuyerById = async () => {
    if (!buyerIdSearch.trim()) {
      notify("Please enter a buyer ID", SnackTypes.error);
      return;
    }
    setIsLoadingBuyers(true);
    try {
      const buyer = await AdminAPIHandler.shared.getBuyerById(buyerIdSearch.trim());
      setBuyers([buyer]);
      setBuyerSearchType('id');
      notify(`Found buyer: ${buyer.fullName}`, SnackTypes.success);
    } catch (e) {
      notify(`Buyer with ID "${buyerIdSearch}" not found`, SnackTypes.error);
      setBuyers([]);
      setBuyerSearchType('id');
    }
    setIsLoadingBuyers(false);
  };

  const fetchBuyerByEmail = async () => {
    if (!buyerEmailSearch.trim()) {
      notify("Please enter a buyer email", SnackTypes.error);
      return;
    }
    setIsLoadingBuyers(true);
    try {
      const buyer = await AdminAPIHandler.shared.getBuyerByEmail(buyerEmailSearch.trim());
      setBuyers([buyer]);
      setBuyerSearchType('email');
      notify(`Found buyer: ${buyer.fullName}`, SnackTypes.success);
    } catch (e) {
      notify(`Buyer with email "${buyerEmailSearch}" not found`, SnackTypes.error);
      setBuyers([]);
      setBuyerSearchType('email');
    }
    setIsLoadingBuyers(false);
  };

  const fetchBuyersByDateRange = async () => {
    if (!buyerStartDate || !buyerEndDate) {
      notify("Please select both start and end dates", SnackTypes.error);
      return;
    }
    setIsLoadingBuyers(true);
    try {
      const buyersList = await AdminAPIHandler.shared.getBuyersByDateRange(buyerStartDate, buyerEndDate);
      setBuyers(buyersList);
      setBuyerSearchType('dateRange');
      notify(`Found ${buyersList.length} buyers`, SnackTypes.success);
    } catch (e) {
      notify("Failed to fetch buyers by date range", SnackTypes.error);
      setBuyers([]);
    }
    setIsLoadingBuyers(false);
  };

  const clearBuyerList = () => {
    setBuyers([]);
    setBuyerSearchType(null);
    setBuyerIdSearch("");
    setBuyerEmailSearch("");
    setBuyerStartDate("");
    setBuyerEndDate("");
    setBuyerFilterQuery("");
    notify("Buyer list cleared", SnackTypes.success);
  };

  const startEditBuyer = (buyer: Buyer) => {
    setBuyerFormState({
      fullName: buyer.fullName,
      email: buyer.email,
      mobileNo: buyer.mobileNo || "",
      about: buyer.about || "",
    });
    setEditingBuyer(buyer);
    setShowBuyerModal(true);
  };

  const saveBuyer = async () => {
    if (!editingBuyer) return;
    
    setIsLoadingBuyers(true);
    try {
      const updated = await AdminAPIHandler.shared.updateBuyerById(editingBuyer.id, buyerFormState);
      setBuyers(prev => prev.map(b => b.id === editingBuyer.id ? updated : b));
      notify("Buyer updated successfully", SnackTypes.success);
      setShowBuyerModal(false);
      setEditingBuyer(null);
    } catch (e) {
      notify("Failed to update buyer", SnackTypes.error);
    }
    setIsLoadingBuyers(false);
  };

  const cancelBuyerEdit = () => {
    setEditingBuyer(null);
    setShowBuyerModal(false);
  };

  // ========== Seller Functions ==========
  
  const fetchSellerById = async () => {
    if (!sellerIdSearch.trim()) {
      notify("Please enter a seller ID", SnackTypes.error);
      return;
    }
    setIsLoadingSellers(true);
    try {
      const seller = await AdminAPIHandler.shared.getSellerById(sellerIdSearch.trim());
      setSellers([seller]);
      setSellerSearchType('id');
      notify(`Found seller: ${seller.fullName}`, SnackTypes.success);
    } catch (e) {
      notify(`Seller with ID "${sellerIdSearch}" not found`, SnackTypes.error);
      setSellers([]);
      setSellerSearchType('id');
    }
    setIsLoadingSellers(false);
  };

  const fetchSellerByEmail = async () => {
    if (!sellerEmailSearch.trim()) {
      notify("Please enter a seller email", SnackTypes.error);
      return;
    }
    setIsLoadingSellers(true);
    try {
      const seller = await AdminAPIHandler.shared.getSellerByEmail(sellerEmailSearch.trim());
      setSellers([seller]);
      setSellerSearchType('email');
      notify(`Found seller: ${seller.fullName}`, SnackTypes.success);
    } catch (e) {
      notify(`Seller with email "${sellerEmailSearch}" not found`, SnackTypes.error);
      setSellers([]);
      setSellerSearchType('email');
    }
    setIsLoadingSellers(false);
  };

  const fetchSellersByDateRange = async () => {
    if (!sellerStartDate || !sellerEndDate) {
      notify("Please select both start and end dates", SnackTypes.error);
      return;
    }
    setIsLoadingSellers(true);
    try {
      const sellersList = await AdminAPIHandler.shared.getSellersByDateRange(sellerStartDate, sellerEndDate);
      setSellers(sellersList);
      setSellerSearchType('dateRange');
      notify(`Found ${sellersList.length} sellers`, SnackTypes.success);
    } catch (e) {
      notify("Failed to fetch sellers by date range", SnackTypes.error);
      setSellers([]);
    }
    setIsLoadingSellers(false);
  };

  const clearSellerList = () => {
    setSellers([]);
    setSellerSearchType(null);
    setSellerIdSearch("");
    setSellerEmailSearch("");
    setSellerStartDate("");
    setSellerEndDate("");
    setSellerFilterQuery("");
    notify("Seller list cleared", SnackTypes.success);
  };

  const startEditSeller = (seller: Seller) => {
    setSellerFormState({
      fullName: seller.fullName,
      email: seller.email,
      occupation: seller.occupation || "",
      personalDescription: seller.personalDescription || "",
      tagline: seller.tagline || "",
      contactNumber: seller.contactNumber || "",
      gender: seller.gender || "",
      planDetails: seller.planDetails || null,
    });
    setEditingSeller(seller);
    setShowSellerModal(true);
  };

  const saveSeller = async () => {
    if (!editingSeller) return;
    
    setIsLoadingSellers(true);
    try {
      const updated = await AdminAPIHandler.shared.updateSellerById(editingSeller.id, sellerFormState);
      setSellers(prev => prev.map(s => s.id === editingSeller.id ? updated : s));
      notify("Seller updated successfully", SnackTypes.success);
      setShowSellerModal(false);
      setEditingSeller(null);
    } catch (e) {
      notify("Failed to update seller", SnackTypes.error);
    }
    setIsLoadingSellers(false);
  };

  const cancelSellerEdit = () => {
    setEditingSeller(null);
    setShowSellerModal(false);
  };

  // Filter functions
  const filteredBuyers = useMemo(() => {
    const q = buyerFilterQuery.trim().toLowerCase();
    if (!q) return buyers;
    return buyers.filter(b =>
      b.fullName.toLowerCase().includes(q) ||
      b.email.toLowerCase().includes(q) ||
      b.id.toLowerCase().includes(q)
    );
  }, [buyerFilterQuery, buyers]);

  const filteredSellers = useMemo(() => {
    const q = sellerFilterQuery.trim().toLowerCase();
    if (!q) return sellers;
    return sellers.filter(s =>
      s.fullName.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q) ||
      s.id.toLowerCase().includes(q)
    );
  }, [sellerFilterQuery, sellers]);

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(`${fieldName} copied to clipboard`, SnackTypes.success);
    } catch (error) {
      notify(`Failed to copy ${fieldName}`, SnackTypes.error);
    }
  };

  return (
    <div className="admin-users-page">
      {/* Compact Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        color: 'white',
        p: 2,
        borderRadius: 2,
        mb: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
          👥 Users Dashboard
        </Typography>
      </Box>

      {/* Main Content Card */}
      <Box sx={{
        background: 'white',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            background: '#f8fafc',
            px: 3,
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              height: 3,
              borderRadius: '2px 2px 0 0'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#64748b',
              '&.Mui-selected': {
                color: '#1e293b'
              }
            }
          }}
        >
          <Tab label="👤 Manage Buyers" />
          <Tab label="🎯 Manage Sellers" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Manage Buyers Tab */}
          {activeTab === 0 && (
            <Box>
              {/* Fetch Controls */}
              <Box sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                p: 2,
                background: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Search by ID"
                    value={buyerIdSearch}
                    onChange={(e) => setBuyerIdSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchBuyerById()}
                    sx={{ minWidth: '250px' }}
                  />
                  <IconButton
                    onClick={fetchBuyerById}
                    disabled={!buyerIdSearch.trim() || isLoadingBuyers}
                    sx={{
                      background: '#3b82f6',
                      color: 'white',
                      '&:hover': {
                        background: '#2563eb'
                      },
                      '&:disabled': {
                        background: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    <PersonSearchIcon />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Search by Email"
                    value={buyerEmailSearch}
                    onChange={(e) => setBuyerEmailSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchBuyerByEmail()}
                    sx={{ minWidth: '250px' }}
                  />
                  <IconButton
                    onClick={fetchBuyerByEmail}
                    disabled={!buyerEmailSearch.trim() || isLoadingBuyers}
                    sx={{
                      background: '#3b82f6',
                      color: 'white',
                      '&:hover': {
                        background: '#2563eb'
                      },
                      '&:disabled': {
                        background: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  alignItems: 'center',
                  p: 1.5,
                  background: 'white',
                  borderRadius: 1,
                  border: '1px dashed #cbd5e1'
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mr: 0.5 }}>
                    📅 Register Date Range:
                  </Typography>
                  <TextField
                    size="small"
                    label="From"
                    type="date"
                    value={buyerStartDate}
                    onChange={(e) => setBuyerStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: '150px' }}
                  />
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>to</Typography>
                  <TextField
                    size="small"
                    label="To"
                    type="date"
                    value={buyerEndDate}
                    onChange={(e) => setBuyerEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: '150px' }}
                  />
                  <IconButton
                    onClick={fetchBuyersByDateRange}
                    disabled={!buyerStartDate || !buyerEndDate || isLoadingBuyers}
                    sx={{
                      background: '#3b82f6',
                      color: 'white',
                      '&:hover': {
                        background: '#2563eb'
                      },
                      '&:disabled': {
                        background: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    <DateRangeIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Status Panel */}
              {buyerSearchType && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  background: '#e3f2fd',
                  border: '1px solid #2196f3',
                  minHeight: '40px'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {buyerSearchType === 'id' ? `🔍 Search Result by ID: "${buyerIdSearch}"` :
                      buyerSearchType === 'email' ? `🔍 Search Result by Email: "${buyerEmailSearch}"` :
                      `📅 Date Range: ${buyerStartDate} to ${buyerEndDate}`} • {buyers.length} {buyers.length === 1 ? 'buyer' : 'buyers'}
                  </Typography>

                  <GGLoaderButton
                    variant="outlined"
                    onClick={clearBuyerList}
                    isLoading={false}
                    size="small"
                    sx={{ color: '#64748b', borderColor: '#d1d5db' }}
                  >
                    Clear
                  </GGLoaderButton>
                </Box>
              )}

              {/* Search Filter */}
              {buyers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Filter current results..."
                    value={buyerFilterQuery}
                    onChange={(e) => setBuyerFilterQuery(e.target.value)}
                    sx={{ width: '300px' }}
                  />
                </Box>
              )}

              {/* Buyers List */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 2 }}>
                {isLoadingBuyers && buyers.length === 0 ? (
                  <Box sx={{
                    textAlign: 'center',
                    p: 4,
                    color: '#64748b',
                    fontSize: '1.1rem',
                    background: '#f8fafc',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0'
                  }}>
                    Loading buyers...
                  </Box>
                ) : filteredBuyers.length === 0 ? (
                  <Box sx={{
                    textAlign: 'center',
                    p: 4,
                    color: '#64748b',
                    background: '#f8fafc',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0'
                  }}>
                    <Typography variant="h6" sx={{ mb: 1, opacity: 0.5 }}>👥</Typography>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>No buyers found</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Search for buyers using ID, email, or date range</Typography>
                  </Box>
                ) : (
                  filteredBuyers.map((buyer) => (
                    <AdminUserCard
                      key={buyer.id}
                      user={buyer}
                      userType="buyer"
                      onEdit={() => startEditBuyer(buyer)}
                      onCopy={copyToClipboard}
                    />
                  ))
                )}
              </Box>
            </Box>
          )}

          {/* Manage Sellers Tab */}
          {activeTab === 1 && (
            <Box>
              {/* Fetch Controls */}
              <Box sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                mb: 3,
                p: 2,
                background: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Search by ID"
                    value={sellerIdSearch}
                    onChange={(e) => setSellerIdSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchSellerById()}
                    sx={{ minWidth: '250px' }}
                  />
                  <IconButton
                    onClick={fetchSellerById}
                    disabled={!sellerIdSearch.trim() || isLoadingSellers}
                    sx={{
                      background: '#8b5cf6',
                      color: 'white',
                      '&:hover': {
                        background: '#7c3aed'
                      },
                      '&:disabled': {
                        background: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    <PersonSearchIcon />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Search by Email"
                    value={sellerEmailSearch}
                    onChange={(e) => setSellerEmailSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchSellerByEmail()}
                    sx={{ minWidth: '250px' }}
                  />
                  <IconButton
                    onClick={fetchSellerByEmail}
                    disabled={!sellerEmailSearch.trim() || isLoadingSellers}
                    sx={{
                      background: '#8b5cf6',
                      color: 'white',
                      '&:hover': {
                        background: '#7c3aed'
                      },
                      '&:disabled': {
                        background: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  alignItems: 'center',
                  p: 1.5,
                  background: 'white',
                  borderRadius: 1,
                  border: '1px dashed #cbd5e1'
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mr: 0.5 }}>
                    📅 Register Date Range:
                  </Typography>
                  <TextField
                    size="small"
                    label="From"
                    type="date"
                    value={sellerStartDate}
                    onChange={(e) => setSellerStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: '150px' }}
                  />
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>to</Typography>
                  <TextField
                    size="small"
                    label="To"
                    type="date"
                    value={sellerEndDate}
                    onChange={(e) => setSellerEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: '150px' }}
                  />
                  <IconButton
                    onClick={fetchSellersByDateRange}
                    disabled={!sellerStartDate || !sellerEndDate || isLoadingSellers}
                    sx={{
                      background: '#8b5cf6',
                      color: 'white',
                      '&:hover': {
                        background: '#7c3aed'
                      },
                      '&:disabled': {
                        background: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    <DateRangeIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Status Panel */}
              {sellerSearchType && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  background: '#f3e8ff',
                  border: '1px solid #8b5cf6',
                  minHeight: '40px'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {sellerSearchType === 'id' ? `🔍 Search Result by ID: "${sellerIdSearch}"` :
                      sellerSearchType === 'email' ? `🔍 Search Result by Email: "${sellerEmailSearch}"` :
                      `📅 Date Range: ${sellerStartDate} to ${sellerEndDate}`} • {sellers.length} {sellers.length === 1 ? 'seller' : 'sellers'}
                  </Typography>

                  <GGLoaderButton
                    variant="outlined"
                    onClick={clearSellerList}
                    isLoading={false}
                    size="small"
                    sx={{ color: '#64748b', borderColor: '#d1d5db' }}
                  >
                    Clear
                  </GGLoaderButton>
                </Box>
              )}

              {/* Search Filter */}
              {sellers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Filter current results..."
                    value={sellerFilterQuery}
                    onChange={(e) => setSellerFilterQuery(e.target.value)}
                    sx={{ width: '300px' }}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: '#94a3b8' }} />
                    }}
                  />
                </Box>
              )}

              {/* Sellers Grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: 2,
                minHeight: '200px'
              }}>
                {isLoadingSellers && sellers.length === 0 ? (
                  <Box sx={{
                    gridColumn: '1 / -1',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px',
                    color: '#64748b'
                  }}>
                    <Typography>Loading sellers...</Typography>
                  </Box>
                ) : filteredSellers.length === 0 ? (
                  <Box sx={{
                    gridColumn: '1 / -1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '300px',
                    color: '#94a3b8'
                  }}>
                    <Typography variant="h6" sx={{ mb: 1, opacity: 0.5 }}>�</Typography>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>No sellers found</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Search for sellers using ID, email, or date range</Typography>
                  </Box>
                ) : (
                  filteredSellers.map((seller) => (
                    <AdminUserCard
                      key={seller.id}
                      user={seller}
                      userType="seller"
                      onEdit={() => startEditSeller(seller)}
                      onCopy={copyToClipboard}
                    />
                  ))
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Buyer Edit Modal */}
      <GGModalWindow 
        open={showBuyerModal} 
        onCloseHandler={cancelBuyerEdit}
        title="👤 Buyer Details"
        childView={
          <Box sx={{ minWidth: { xs: '100%', sm: '500px', md: '600px' } }}>
            {/* User ID Banner */}
            <Box sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              p: 2,
              borderRadius: 2,
              mb: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                  User ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                  {editingBuyer?.id}
                </Typography>
              </Box>
              <Box sx={{
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 1,
                px: 1.5,
                py: 0.5
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  BUYER
                </Typography>
              </Box>
            </Box>

            {/* Form Content */}
            <Box sx={{ px: 1 }}>
              {/* Profile Image Section */}
              {editingBuyer?.profileImage && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Box sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    margin: '0 auto',
                    border: '3px solid #3b82f6',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}>
                    <img 
                      src={editingBuyer.profileImage} 
                      alt={editingBuyer.fullName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                </Box>
              )}

              {/* Basic Information Section */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  📋 Basic Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Full Name"
                    value={buyerFormState.fullName}
                    disabled
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        background: '#f8fafc'
                      }
                    }}
                  />
                  <TextField
                    label="Email Address"
                    value={buyerFormState.email}
                    disabled
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        background: '#f8fafc'
                      }
                    }}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                      label="Mobile Number"
                      value={buyerFormState.mobileNo || 'Not provided'}
                      disabled
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          background: '#f8fafc'
                        }
                      }}
                    />
                    <TextField
                      label="Mobile Verified"
                      value={editingBuyer?.mobileNoVerified ? 'Yes' : 'No'}
                      disabled
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          background: '#f8fafc'
                        }
                      }}
                    />
                  </Box>
                  <TextField
                    label="Registered On"
                    value={editingBuyer?.createdAt ? new Date(editingBuyer.createdAt).toLocaleDateString("en-IN", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }) : 'N/A'}
                    disabled
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        background: '#f8fafc'
                      }
                    }}
                  />
                  {editingBuyer?.seller && (
                    <TextField
                      label="Also a Seller (Seller ID)"
                      value={editingBuyer.seller}
                      disabled
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          background: '#fff3cd',
                          borderLeft: '3px solid #8b5cf6'
                        }
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* About Section */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  💬 About
                </Typography>
                <TextField
                  value={buyerFormState.about || 'No description provided'}
                  disabled
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      background: '#f8fafc'
                    }
                  }}
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                pt: 2,
                borderTop: '1px solid #e2e8f0'
              }}>
                <Button 
                  variant="outlined" 
                  onClick={cancelBuyerEdit}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#cbd5e1',
                    color: '#64748b',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      background: '#f8fafc'
                    }
                  }}
                >
                  Cancel
                </Button>
                <GGLoaderButton
                  variant="contained"
                  onClick={saveBuyer}
                  isLoading={isLoadingBuyers}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                    }
                  }}
                >
                  Save Changes
                </GGLoaderButton>
              </Box>
            </Box>
          </Box>
        }
      />

      {/* Seller Edit Modal */}
      <GGModalWindow 
        open={showSellerModal} 
        onCloseHandler={cancelSellerEdit}
        title="💼 Seller Details"
        childView={
          <Box sx={{ minWidth: { xs: '100%', sm: '500px', md: '600px' } }}>
            {/* User ID Banner */}
            <Box sx={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white',
              p: 2,
              borderRadius: 2,
              mb: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                  Seller ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                  {editingSeller?.id}
                </Typography>
              </Box>
              <Box sx={{
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 1,
                px: 1.5,
                py: 0.5
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  SELLER
                </Typography>
              </Box>
            </Box>

            {/* Form Content */}
            <Box sx={{ px: 1 }}>
              {/* Profile Image Section */}
              {editingSeller?.profileImage && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Box sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    margin: '0 auto',
                    border: '3px solid #8b5cf6',
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
                  }}>
                    <img 
                      src={editingSeller.profileImage} 
                      alt={editingSeller.fullName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                </Box>
              )}

              {/* Basic Information Section */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  📋 Basic Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Full Name"
                    value={sellerFormState.fullName}
                    disabled
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        background: '#f8fafc'
                      }
                    }}
                  />
                  <TextField
                    label="Email Address"
                    value={sellerFormState.email}
                    disabled
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        background: '#f8fafc'
                      }
                    }}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                      label="Contact Number"
                      value={sellerFormState.contactNumber || 'Not provided'}
                      disabled
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          background: '#f8fafc'
                        }
                      }}
                    />
                    <TextField
                      label="Gender"
                      value={sellerFormState.gender || 'Not specified'}
                      disabled
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          background: '#f8fafc'
                        }
                      }}
                    />
                  </Box>
                  <TextField
                    label="Date of Birth"
                    value={editingSeller?.dateOfBirth ? new Date(editingSeller.dateOfBirth).toLocaleDateString("en-IN", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }) : 'Not provided'}
                    disabled
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        background: '#f8fafc'
                      }
                    }}
                  />
                  {editingSeller?.location && (
                    <TextField
                      label="Location"
                      value={`${editingSeller.location.state}, ${editingSeller.location.country}`}
                      disabled
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          background: '#f8fafc'
                        }
                      }}
                    />
                  )}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                      label="User Verified"
                      value={editingSeller?.userVerified ? 'Yes ✓' : 'No'}
                      disabled
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          background: editingSeller?.userVerified ? '#d1fae5' : '#f8fafc'
                        }
                      }}
                    />
                    <TextField
                      label="Agreement Signed"
                      value={editingSeller?.userAgreement ? 'Yes ✓' : 'No'}
                      disabled
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          background: editingSeller?.userAgreement ? '#dbeafe' : '#f8fafc'
                        }
                      }}
                    />
                  </Box>
                  {editingSeller?.buyer && (
                    <TextField
                      label="Also a Buyer (Buyer ID)"
                      value={editingSeller.buyer}
                      disabled
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          background: '#dbeafe',
                          borderLeft: '3px solid #3b82f6'
                        }
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Professional Information Section */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  💼 Professional Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Occupation"
                    value={sellerFormState.occupation || 'Not provided'}
                    disabled
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        background: '#f8fafc'
                      }
                    }}
                  />
                  <TextField
                    label="Tagline"
                    value={sellerFormState.tagline || 'Not provided'}
                    disabled
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        background: '#f8fafc'
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Personal Description Section */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  💬 Personal Description
                </Typography>
                <TextField
                  value={sellerFormState.personalDescription || 'No description provided'}
                  disabled
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      background: '#f8fafc'
                    }
                  }}
                />
              </Box>

              {/* Seller Plan Section */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#64748b',
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  💼 Seller Plan
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Assigned Plan</InputLabel>
                    <Select
                      label="Assigned Plan"
                      value={sellerFormState.planDetails?.plan || ''}
                      onChange={(e) => {
                        const selectedPlanId = e.target.value;
                        if (!selectedPlanId) {
                          setSellerFormState((s: any) => ({ ...s, planDetails: null }));
                        } else {
                          // Set default expiry to 1 year from now if creating new plan
                          let expiryDate: Date;
                          if (sellerFormState.planDetails?.expiryTimeStamp) {
                            expiryDate = new Date(sellerFormState.planDetails.expiryTimeStamp);
                          } else {
                            expiryDate = new Date();
                            expiryDate.setFullYear(expiryDate.getFullYear() + 1);
                            // Set time to 23:59:59
                            expiryDate.setHours(23, 59, 59, 999);
                          }
                          
                          setSellerFormState((s: any) => ({
                            ...s,
                            planDetails: {
                              plan: selectedPlanId,
                              expiryTimeStamp: expiryDate.toISOString()
                            }
                          }));
                        }
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          background: 'white'
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>No Plan (Default Fees)</em>
                      </MenuItem>
                      {availablePlans.map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>
                          {plan.name} ({plan.sellerPlatformFees.feesPercent}% fees)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {sellerFormState.planDetails && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Plan Expiry Date"
                        value={
                          sellerFormState.planDetails.expiryTimeStamp
                            ? dayjs(sellerFormState.planDetails.expiryTimeStamp)
                            : null
                        }
                        onChange={(newDate) => {
                          if (newDate) {
                            // Set time to 23:59:59
                            const dateWithTime = newDate.hour(23).minute(59).second(59).millisecond(999);
                            setSellerFormState((s: any) => ({
                              ...s,
                              planDetails: {
                                ...s.planDetails,
                                expiryTimeStamp: dateWithTime.toISOString()
                              }
                            }));
                          }
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            helperText: sellerFormState.planDetails.expiryTimeStamp &&
                              new Date(sellerFormState.planDetails.expiryTimeStamp) < new Date()
                                ? '⚠️ Plan has expired'
                                : sellerFormState.planDetails.expiryTimeStamp
                                ? `Expires on ${new Date(sellerFormState.planDetails.expiryTimeStamp).toLocaleDateString('en-IN', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}`
                                : '',
                            sx: {
                              '& .MuiInputBase-root': {
                                background: 'white'
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  )}

                  {sellerFormState.planDetails && sellerFormState.planDetails.plan && (
                    <Box sx={{
                      p: 2,
                      background: '#f0f9ff',
                      borderRadius: 2,
                      border: '1px solid #3b82f6'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', display: 'block', mb: 1 }}>
                        📋 Plan Details:
                      </Typography>
                      {(() => {
                        const plan = availablePlans.find(p => p.id === sellerFormState.planDetails.plan);
                        if (!plan) return <Typography variant="caption" sx={{ color: '#64748b' }}>Plan not found</Typography>;
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#1e40af' }}>
                              • Platform Fees: {plan.sellerPlatformFees.feesPercent}%
                            </Typography>
                            {plan.sellerPlatformFees.maxCap && (
                              <Typography variant="caption" sx={{ color: '#1e40af' }}>
                                • Max Cap: ₹{(plan.sellerPlatformFees.maxCap / 100).toFixed(2)}
                              </Typography>
                            )}
                            {plan.maxGigsAllowed && (
                              <Typography variant="caption" sx={{ color: '#1e40af' }}>
                                • Max Gigs: {plan.maxGigsAllowed}
                              </Typography>
                            )}
                            {plan.maxOrdersPerMonth && (
                              <Typography variant="caption" sx={{ color: '#1e40af' }}>
                                • Max Orders/Month: {plan.maxOrdersPerMonth}
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                pt: 2,
                borderTop: '1px solid #e2e8f0'
              }}>
                <Button 
                  variant="outlined" 
                  onClick={cancelSellerEdit}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#cbd5e1',
                    color: '#64748b',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      background: '#f8fafc'
                    }
                  }}
                >
                  Cancel
                </Button>
                <GGLoaderButton
                  variant="contained"
                  onClick={saveSeller}
                  isLoading={isLoadingSellers}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                    }
                  }}
                >
                  Save Changes
                </GGLoaderButton>
              </Box>
            </Box>
          </Box>
        }
      />

      <SnackBar 
        ref={snackBarRef} 
        message={snackBarParams.message} 
        severity={snackBarParams.state}
        alertDuration={5000}
      />
    </div>
  );
};

export default AdminUsersPage;

